import React, { useState } from "react";
import {
  Group,
  Text,
  Button,
  Modal,
  FileInput,
  Anchor,
} from "@mantine/core";
import {
  IconCircleCheck,
  IconExclamationCircle,
  IconDownload,
} from "@tabler/icons";
import { useTranslation } from "react-i18next";
import { showNotification } from "@mantine/notifications";
import { useServerApi } from "../hooks/userServerApi";
import { getImportErrorMessage } from "../helpers/importResultUtils";
import { authName } from "../App";

const userRoleAccessTemplateUrl = `${process.env.REACT_APP_SERVER_URL}/userRole/downloadTemplate`;

const formatRoleAccessImportSummary = (data) => {
  if (!data) return "Import failed";
  if (typeof data === "string") return data;
  const lines = [];
  if (data.message) lines.push(data.message);
  if (data.skipped?.length) {
    lines.push(`Skipped unknown role(s): ${data.skipped.join(", ")}`);
  }
  (data.warnings ?? []).forEach((warning) => lines.push(`- ${warning}`));
  return lines.filter(Boolean).join("\n") || "Import completed.";
};

const UserRoleAccessImportModal = ({ opened, onClose, onImported }) => {
  const [api] = useServerApi();
  const { t } = useTranslation();
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);

  const downloadAccessTemplate = async () => {
    try {
      const token = localStorage.getItem(authName);
      const result = await fetch(userRoleAccessTemplateUrl, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "x-access-token": token,
        },
      });

      const contentType = result.headers.get("Content-Type") ?? "";
      if (!result.ok || contentType.includes("application/json")) {
        const errorBody = contentType.includes("application/json")
          ? await result.json()
          : null;
        throw new Error(
          errorBody?.message ?? errorBody?.error ?? "Failed to download template"
        );
      }

      const blob = await result.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = blobUrl;
      const contentDisposition = result.headers.get("Content-Disposition");
      let fileName = "user-role-access-right.xlsx";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match?.[1]) fileName = match[1];
      }
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      showNotification({
        title: "Download failed",
        color: "red",
        icon: <IconExclamationCircle size={18} />,
        message: error?.message ?? String(error),
      });
    }
  };

  const handleImport = async () => {
    if (!importFile) return;
    try {
      setImporting(true);
      const result = await api.importBatch({
        apiEntity: "userRole",
        file: importFile,
      });
      if (!result?.success) {
        throw result?.error ?? { message: result?.message ?? "Import failed" };
      }

      const importSummary = result.data;
      const warnings = importSummary?.warnings ?? [];
      const skipped = importSummary?.skipped ?? [];
      const hasWarnings = warnings.length > 0 || skipped.length > 0;

      showNotification({
        title: hasWarnings
          ? `${importFile.name} Import completed with warnings`
          : `${importFile.name} Import successfully`,
        color: hasWarnings ? "yellow" : "green",
        icon: hasWarnings ? (
          <IconExclamationCircle size={18} />
        ) : (
          <IconCircleCheck size={18} />
        ),
        message: (
          <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
            {formatRoleAccessImportSummary(importSummary)}
          </Text>
        ),
        autoClose: hasWarnings ? false : undefined,
      });
      setImportFile(null);
      onImported?.();
      onClose?.();
    } catch (error) {
      console.log(error);
      showNotification({
        title: `${importFile?.name ?? "File"} Import failed`,
        icon: <IconExclamationCircle size={18} />,
        color: "red",
        message: (
          <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
            {getImportErrorMessage(error)}
          </Text>
        ),
        autoClose: false,
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={() => {
        if (importing) return;
        onClose?.();
      }}
      title={t("Import Access Right")}
      size="xl"
    >
      <FileInput
        placeholder="Pick file"
        label="Data File"
        withAsterisk
        value={importFile}
        onChange={setImportFile}
        disabled={importing}
      />
      <Group mt="md" spacing="xs">
        <IconDownload size="1rem" style={{ marginRight: "0.5rem" }} />
        <Anchor
          size="xs"
          component="button"
          type="button"
          onClick={downloadAccessTemplate}
        >
          {t("Download Access Right Template")}
        </Anchor>
      </Group>
      <Button
        onClick={handleImport}
        mt="xl"
        size="xs"
        loading={importing}
        disabled={!importFile}
      >
        {t("Import")}
      </Button>
    </Modal>
  );
};

export default UserRoleAccessImportModal;
